import router from "next/router";
import { useState, useEffect, useCallback } from "react";

import { getBatch, getRef } from "lib/firebase";
import {
  collection,
  getDocs,
  getDoc,
  increment,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import UUID from "uuidjs";
import { convertDate } from "lib/utils";
import { sendNotifMail } from "lib/sendEmail";
import { createNotif } from "lib/createNotif";

import { CurrentUser, Comment } from "lib/type";

let posting = false;

export const useComments = (
  author: CurrentUser,
  user: CurrentUser,
  articleID: string,
  articleTitle: string
) => {
  const [loadedComment, setLoadedComment] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  useEffect(() => {
    if (!author || !articleID || loadedComment) return;
    const loadComments = async () => {
      const articlePath = `${author.uid}/publicArticles/${articleID}`;
      const articleRef = getRef("users", articlePath);
      const commentsRefs = collection(articleRef, "comments");
      const q = query(commentsRefs, orderBy("createdAt", "desc"), limit(10));
      const commentsDocs = await getDocs(q).catch((err) => {
        console.log(err);
        alert("コメント情報の取得に失敗しました。");
        return null;
      });
      if (!commentsDocs || commentsDocs.empty) return setLoadedComment(true);
      const commentDatasRaw = await Promise.all(
        commentsDocs.docs.map(async (commentDoc) => {
          const commentData = commentDoc.data();
          const { commentID, commentUid, body, likeCount, createdAt } =
            commentData;
          const commentUserRef = getRef("users", commentUid);
          const commentUserDoc = await getDoc(commentUserRef).catch((err) => {
            console.log(err);
            return null;
          });
          if (!commentUserDoc || !commentUserDoc.exists()) return;
          const commentUser = commentUserDoc.data() as CurrentUser;
          const date = convertDate(createdAt);
          const commentObj: Comment = {
            articleID: articleID,
            commentID: commentID,
            commentUser: commentUser,
            body: body,
            likeCount: likeCount,
            createdAt: date,
          };
          return commentObj;
        })
      );
      if (commentDatasRaw.length < 1) return;
      const commentDatas = commentDatasRaw.filter(
        (comment) => comment
      ) as Comment[];
      setComments(commentDatas);
      setLoadedComment(true);
    };
    loadComments();
  }, [author, articleID, loadedComment]);

  const [commentCount, setCommentCount] = useState(0);
  useEffect(() => {
    if (!author || !articleID) return;
    const loadCommentCount = async () => {
      const articleRef = getRef(
        "users",
        `${author.uid}/publicArticles/${articleID}`
      );
      const articleDoc = await getDoc(articleRef).catch((err) => {
        console.log(err);
        alert("コメント数の取得に失敗しました。");
        return null;
      });
      if (!articleDoc || !articleDoc.exists()) return;
      const articleData = articleDoc.data();
      setCommentCount(articleData.commentCount);
    };
    loadCommentCount();
  }, [articleID, author]);

  const [inputComment, setInputComment] = useState("");
  const changeComment = ({
    target: { value },
  }: {
    target: { value: string };
  }) => setInputComment(value);

  const postComment = async () => {
    if (posting || !author || !user || !articleID || !inputComment) return;
    posting = true;
    const articlePath = `${author.uid}/publicArticles/${articleID}`;
    const articleRef = getRef("users", articlePath);
    const commentID = UUID.generate();
    const commentRef = getRef("users", `${articlePath}/comments/${commentID}`);
    const batch = getBatch();
    batch.set(commentRef, {
      articleID: articleID,
      commentID: commentID,
      commentUid: user.uid,
      body: inputComment,
      likeCount: 0,
      createdAt: serverTimestamp(),
    });
    batch.update(articleRef, {
      commentCount: increment(1),
    });
    if (user.uid !== author.uid)
      await createNotif(
        "comment",
        batch,
        { id: articleID, title: articleTitle },
        user,
        author
      );
    batch
      .commit()
      .then(() => {
        if (author.uid !== user.uid && author.isSendNotif)
          sendNotifMail(author, user, "comment", articleID, articleTitle);
        router.reload();
      })
      .catch((err) => {
        console.log(err);
        alert("コメントの投稿に失敗しました。");
        return;
      });
  };
  useEffect(() => {
    return () => {
      posting = false;
    };
  }, []);

  const deleteComment = useCallback(
    (commentID: string, commentUid: string) => {
      if (
        !author ||
        !user ||
        !articleID ||
        !commentID ||
        user.uid !== commentUid
      )
        return;
      const articlePath = `${author.uid}/publicArticles/${articleID}`;
      const articleRef = getRef("users", articlePath);
      const commentRef = getRef(
        "users",
        `${articlePath}/comments/${commentID}`
      );
      const batch = getBatch();
      batch.delete(commentRef);
      batch.update(articleRef, {
        commentCount: increment(-1),
      });
      batch
        .commit()
        .then(() => router.reload())
        .catch((err) => {
          console.log(err);
          alert("コメントの削除に失敗しました。");
        });
    },
    [articleID, author, user]
  );

  return {
    loadedComment,
    comments,
    commentCount,
    changeComment,
    postComment,
    deleteComment,
  };
};
